import { getAuth } from "@clerk/remix/ssr.server";
import { LoaderFunction } from "@remix-run/cloudflare";
import { createClient } from "@supabase/supabase-js";
import {
  getCurrentUserLeaderboardObj,
  getLeaderboard,
} from "~/dao/LeaderboardDAO";
import { Database } from "~/types/supabase";
import { createClerkClient } from "@clerk/remix/api.server";
import { useLoaderData } from "@remix-run/react";
import { RichUserType } from "~/types/user";
import { Avatar, cn } from "@heroui/react";
import { formatNumber } from "~/utils/formatters";

import { motion } from "framer-motion";

export const loader: LoaderFunction = async (args) => {
  const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
  const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;
  const clerkSecret = args.context.cloudflare.env.CLERK_SECRET_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new Response("Supabase credentials are missing", { status: 500 });
  }

  const supabase = createClient<Database>(supabaseUrl, supabaseKey);
  const clerkClient = await createClerkClient({ secretKey: clerkSecret });
  const data = await getLeaderboard(clerkClient, supabase);

  const { userId } = await getAuth(args);

  const payload = {
    users: data,
    currentUser: userId
      ? await getCurrentUserLeaderboardObj(clerkClient, supabase, userId)
      : null,
  };

  return payload;
};

export default function Leaderboard() {
  const data = useLoaderData<{
    users: RichUserType[];
    currentUser: RichUserType | null;
  }>();

  return (
    <div className="flex flex-col items-center w-full max-w-2xl h-dvh overflow-y-scroll relative  font-chewy pb-20 pt-10">
      {data.currentUser && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="fixed bottom-16 flex flex-row justify-center left-0 right-0 z-10"
        >
          <LeaderboardRow
            user={data.currentUser}
            rank={data.currentUser.globalRank ?? -1}
            className="bg-neutral-700"
          />
        </motion.div>
      )}
      <h1>Leaderboard</h1>
      <ul className="flex flex-col justify-center items-center w-full">
        {data.users.map((user, index) => (
          <a
            key={index}
            href={`/leaderboard/user/${user.user_id}/results`}
            className="w-full"
          >
            <LeaderboardRow
              user={user}
              rank={index + 1}
              className={
                data.currentUser?.user_id === user.user_id
                  ? "bg-neutral-700"
                  : ""
              }
            />
          </a>
        ))}
      </ul>
    </div>
  );
}

const LeaderboardRow = ({
  user,
  rank,
  className,
}: {
  user: RichUserType;
  rank: number;
  className?: string;
}) => {
  return (
    <div
      key={user.user_id}
      className={cn(
        "flex flex-row w-full max-w-2xl justify-between items-center gap-2 px-3 py-2",
        className
      )}
    >
      <div className="flex flex-row items-center gap-3">
        <p className="text-center text-xl w-10">{rank}</p>
        <Avatar src={user.user?.imageUrl} showFallback />
        <p className="text-xl">{user.user?.username ?? "User"} </p>
      </div>
      <p className="font-mono tracking-tighter mr-2">
        {formatNumber(user.overall_xp)} XP
      </p>
    </div>
  );
};
